import { Controller } from "@hotwired/stimulus"
import Rails from "@rails/ujs";
export default class extends Controller {

  open(e) {
    e.stopPropagation();
    e.preventDefault();

    // Make sure to remove any existing input first to avoid multiple inputs
    if (this.hasInput) {
      return;
    }

    // Check if the cell is locked (done_date before 31/12/2025 for non-admins)
    if (e.currentTarget.dataset.locked === 'true') {
      return;
    }

    // Use currentTarget (the element with data-action) not target (could be a child element)
    this.cell = e.currentTarget
    this.url = this.cell.dataset.url
    this.fieldType = this.cell.dataset.fieldType || 'text'
    this.fieldOptions = this.cell.dataset.fieldOptions ? JSON.parse(this.cell.dataset.fieldOptions) : null

    // Save the current text for restoration if needed
    const currentValue = this.cell.textContent.trim();
    this.originalValue = currentValue;

    let inputElement;

    switch (this.fieldType) {
      case 'boolean':
        inputElement = this.createCheckbox(this.cell.dataset.fieldValue === 'true');
        break;
      case 'select':
        inputElement = this.createSelect(currentValue);
        break;
      case 'date':
        inputElement = this.createDateInput(currentValue);
        break;
      case 'month':
        inputElement = this.createMonthInput(this.cell.dataset.monthValue);
        break;
      default:
        inputElement = this.createTextInput(currentValue);
    }

    // Replace the cell content with the input
    this.cell.innerHTML = '';
    this.cell.appendChild(inputElement);
    inputElement.focus();

    this.hasInput = true;
  }

  createTextInput(currentValue) {
    const input = document.createElement('input');
    input.type = 'text';
    input.value = currentValue;
    input.classList.add('w-full', 'text-xs', 'p-1', 'border', 'border-gray-300', 'rounded');

    input.addEventListener('keypress', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        this.save(input.value, input.value);
      }
    });

    input.addEventListener('blur', () => {
      this.save(input.value, input.value);
    });

    return input;
  }

  createDateInput(currentValue) {
    const input = document.createElement('input');
    input.type = 'date';

    // Convertir la date affichée (dd/mm/yyyy) en format ISO (yyyy-mm-dd)
    if (currentValue) {
      const parts = currentValue.split('/');
      if (parts.length === 3) {
        input.value = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }

    input.classList.add('w-full', 'text-xs', 'p-1', 'border', 'border-gray-300', 'rounded');

    input.addEventListener('change', () => {
      // Convertir le format ISO en format d'affichage
      const displayValue = this.formatDateForDisplay(input.value);
      this.save(input.value, displayValue);
    });

    input.addEventListener('blur', () => {
      this.cancel();
    });

    return input;
  }

  createMonthInput(currentValue) {
    const input = document.createElement('input');
    input.type = 'month';

    // currentValue au format YYYY-MM (ex: "2026-03")
    if (currentValue) {
      input.value = currentValue;
    }

    input.classList.add('w-full', 'text-xs', 'p-1', 'border', 'border-gray-300', 'rounded');

    input.addEventListener('change', () => {
      if (input.value) {
        // Formater pour l'affichage : "mars 2026"
        const [year, month] = input.value.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, 1);
        const displayValue = date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
        this.save(input.value, displayValue);
      } else {
        this.save('', '');
      }
    });

    input.addEventListener('blur', () => {
      this.cancel();
    });

    return input;
  }

  createCheckbox(isChecked) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('flex', 'items-center', 'justify-center', 'p-1');

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.checked = isChecked;
    input.classList.add('w-4', 'h-4', 'text-indigo-600', 'border-gray-300', 'rounded', 'cursor-pointer');

    input.addEventListener('change', () => {
      this.save(input.checked, input.checked ? '✅' : '');
    });

    wrapper.appendChild(input);
    return wrapper;
  }

  createSelect(currentValue) {
    const select = document.createElement('select');
    select.classList.add('w-full', 'text-xs', 'p-1', 'border', 'border-gray-300', 'rounded');

    // Option vide
    const emptyOption = document.createElement('option');
    emptyOption.value = '';
    emptyOption.textContent = '';
    select.appendChild(emptyOption);

    // Ajouter les options
    if (this.fieldOptions) {
      this.fieldOptions.forEach(opt => {
        const option = document.createElement('option');
        // opt peut être [label, value] ou juste une string
        if (Array.isArray(opt)) {
          option.value = opt[1];
          option.textContent = opt[0];
          if (opt[0] === currentValue || opt[1].toString() === currentValue) {
            option.selected = true;
          }
        } else {
          option.value = opt;
          option.textContent = opt;
          if (opt === currentValue) {
            option.selected = true;
          }
        }
        select.appendChild(option);
      });
    }

    select.addEventListener('change', () => {
      const selectedOption = select.options[select.selectedIndex];
      if (select.value === '') {
        // Si rien sélectionné, restaurer la valeur originale
        this.cancel();
      } else {
        this.save(select.value, selectedOption.textContent);
      }
    });

    select.addEventListener('blur', () => {
      const selectedOption = select.options[select.selectedIndex];
      if (select.value === '') {
        // Si rien sélectionné, restaurer la valeur originale
        this.cancel();
      } else {
        this.save(select.value, selectedOption.textContent);
      }
    });

    return select;
  }

  formatDateForDisplay(isoDate) {
    if (!isoDate) return '';
    const parts = isoDate.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return isoDate;
  }

  cancel() {
    // Restaurer la valeur originale sans sauvegarder
    this.cell.textContent = this.originalValue;
    this.hasInput = false;
  }

  save(newValue, displayValue) {
    // Pour les associations (champs avec _id), utiliser le bon nom d'attribut
    let attribute = this.cell.dataset.attribute;
    const isPhase = this.cell.dataset.isPhase === 'true';

    // Si c'est un champ select avec une valeur numérique, c'est probablement un ID
    if (!isPhase && this.fieldType === 'select' && newValue && !isNaN(newValue)) {
      // Ajouter _id si ce n'est pas déjà présent
      if (!attribute.endsWith('_id')) {
        attribute = attribute + '_id';
      }
    }

    let params;
    if (isPhase) {
      const phaseId = this.cell.dataset.phaseId;
      params = { [this.cell.dataset.klass]: { phase_attributes: { id: phaseId, [attribute]: newValue } } };
    } else {
      params = { [this.cell.dataset.klass]: { [attribute]: newValue } };
    }
    // Get the CSRF token from the meta tag
    const csrfToken = document.querySelector("[name='csrf-token']").content;

    fetch(this.url, {
      method: 'PATCH',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'include',
      body: JSON.stringify(params),
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      // Update the cell with the display value
      this.cell.textContent = displayValue;
      this.hasInput = false;
    })
    .catch(error => {
      console.error('Error updating the value:', error);
      this.hasInput = false;
    });
  }

}
